
import React from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, Calendar, User, Clock } from 'lucide-react';

interface PaymentSummaryProps {
  teacherName: string;
  baseSalary: number;
  paymentStatus: 'paid' | 'pending';
  lastPaymentDate?: string | null;
  currentPaymentPeriod?: string;
  allowances?: Array<{ type: string; amount: number }>;
  deductions?: Array<{ type: string; amount: number }>;
}

export const PaymentSummary = ({ 
  teacherName, 
  baseSalary, 
  paymentStatus, 
  lastPaymentDate, 
  currentPaymentPeriod,
  allowances = [],
  deductions = []
}: PaymentSummaryProps) => {
  const calculateNetSalary = () => {
    const totalAllowances = allowances.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    const totalDeductions = deductions.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    return baseSalary + totalAllowances - totalDeductions;
  };

  const netSalary = calculateNetSalary();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Payment Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Base Salary</p>
            <p className="font-semibold">{baseSalary.toLocaleString()} AFN</p>
          </div>
        </div>
        
        {(allowances.length > 0 || deductions.length > 0) && (
          <div className="flex items-center gap-3">
            <DollarSign className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-muted-foreground">Net Salary</p>
              <p className="font-semibold">{netSalary.toFixed(2)} AFN</p>
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Current Status</p>
            <Badge variant={paymentStatus === 'paid' ? 'default' : 'destructive'}>
              {paymentStatus === 'paid' ? 'Paid' : 'Pending'}
            </Badge>
          </div>
        </div>
        
        {currentPaymentPeriod && (
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-sm text-muted-foreground">Payment Period</p>
              <p className="font-semibold">{currentPaymentPeriod}</p>
            </div>
          </div>
        )}
        
        {lastPaymentDate && (
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-orange-600" />
            <div>
              <p className="text-sm text-muted-foreground">Last Payment</p>
              <p className="font-semibold">
                {format(new Date(lastPaymentDate), 'PPP')}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
